import { useState, useCallback, useRef } from 'react'
import { X, Upload, ArrowRight, ArrowLeft, Check, AlertCircle, FileText, Table, Copy } from 'lucide-react'
import { parseFile } from '@/core/import/parser'
import type { ParsedData } from '@/core/import/parser'
import { autoMapColumns, buildImportPreview, MAPPABLE_FIELDS } from '@/core/import/mapper'
import type { ColumnMapping, ImportPreview } from '@/core/import/mapper'
import { executeImport } from '@/core/import/executor'
import type { ImportResult } from '@/core/import/executor'

// ---------------------------------------------------------------------------
// Step types
// ---------------------------------------------------------------------------

type ImportStep = 'upload' | 'mapping' | 'preview' | 'result'

interface Props {
  onClose: () => void
  onImportComplete: () => void
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ImportModal({ onClose, onImportComplete }: Props) {
  const [step, setStep] = useState<ImportStep>('upload')
  const [parsedData, setParsedData] = useState<ParsedData | null>(null)
  const [fileName, setFileName] = useState('')
  const [mappings, setMappings] = useState<ColumnMapping[]>([])
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [importing, setImporting] = useState(false)
  const [skipDuplicates, setSkipDuplicates] = useState(true)
  const [dragOver, setDragOver] = useState(false)
  const [parseError, setParseError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // -------------------------------------------------------------------------
  // Step 1: File upload
  // -------------------------------------------------------------------------

  const handleFile = useCallback(async (file: File) => {
    setParseError(null)
    setFileName(file.name)

    const validExtensions = ['.csv', '.json']
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()
    if (!validExtensions.includes(ext)) {
      setParseError('Please upload a CSV or JSON file')
      return
    }

    try {
      const data = await parseFile(file)
      if (data.errors.length > 0 && data.totalRows === 0) {
        setParseError(data.errors.map(e => e.message).join('. '))
        return
      }
      setParsedData(data)

      // Auto-map columns
      const autoMappings = autoMapColumns(data.headers)
      setMappings(autoMappings)

      setStep('mapping')
    } catch (err) {
      setParseError(`Failed to parse file: ${(err as Error).message}`)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }, [handleFile])

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }, [handleFile])

  // -------------------------------------------------------------------------
  // Step 2: Column mapping -> Preview
  // -------------------------------------------------------------------------

  const handleNextToPreview = useCallback(async () => {
    if (!parsedData) return

    const preview = await buildImportPreview(parsedData.rows, mappings)
    setPreview(preview)
    setStep('preview')
  }, [parsedData, mappings])

  // -------------------------------------------------------------------------
  // Step 3: Preview -> Execute import
  // -------------------------------------------------------------------------

  const handleImport = useCallback(async () => {
    if (!preview) return

    setImporting(true)
    try {
      const result = await executeImport(preview.validations, {
        skipDuplicates,
        importDuplicates: !skipDuplicates,
      })
      setImportResult(result)
      setStep('result')

      if (result.succeeded > 0) {
        onImportComplete()
      }
    } catch (err) {
      const currentPreview = preview
      setImportResult({
        succeeded: 0,
        failed: currentPreview.validations.length,
        skippedDuplicates: 0,
        errors: [{ rowIndex: 0, message: (err as Error).message }],
      })
      setStep('result')
    } finally {
      setImporting(false)
    }
  }, [preview, skipDuplicates, onImportComplete])

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Import Applications</h3>
            <StepIndicator currentStep={step} />
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {step === 'upload' && (
            <UploadStep
              dragOver={dragOver}
              onDragOver={setDragOver}
              onDrop={handleDrop}
              onFileInput={handleFileInput}
              fileInputRef={fileInputRef}
              error={parseError}
            />
          )}
          {step === 'mapping' && parsedData && (
            <MappingStep
              parsedData={parsedData}
              mappings={mappings}
              onMappingsChange={setMappings}
              fileName={fileName}
            />
          )}
          {step === 'preview' && preview && (
            <PreviewStep
              preview={preview}
              skipDuplicates={skipDuplicates}
              onSkipDuplicatesChange={setSkipDuplicates}
            />
          )}
          {step === 'result' && importResult && (
            <ResultStep result={importResult} />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div>
            {step !== 'upload' && step !== 'result' && (
              <button
                onClick={() => setStep(step === 'mapping' ? 'upload' : step === 'preview' ? 'mapping' : 'upload')}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              {step === 'result' ? 'Done' : 'Cancel'}
            </button>
            {step === 'mapping' && (
              <button
                onClick={handleNextToPreview}
                disabled={mappings.length === 0}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next: Preview
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {step === 'preview' && preview && (
              <button
                onClick={handleImport}
                disabled={importing || (preview.newCount === 0 && skipDuplicates)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Import {skipDuplicates ? preview.newCount : preview.validRows} Applications
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step indicator
// ---------------------------------------------------------------------------

function StepIndicator({ currentStep }: { currentStep: ImportStep }) {
  const steps = [
    { key: 'upload', label: '1' },
    { key: 'mapping', label: '2' },
    { key: 'preview', label: '3' },
    { key: 'result', label: '4' },
  ]

  const currentIndex = steps.findIndex(s => s.key === currentStep)

  return (
    <div className="flex items-center gap-1">
      {steps.map((s, i) => (
        <div
          key={s.key}
          className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${
            i < currentIndex
              ? 'bg-emerald-500 text-white'
              : i === currentIndex
              ? 'bg-blue-600 text-white'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
          }`}
        >
          {i < currentIndex ? <Check className="w-3 h-3" /> : s.label}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 1: Upload
// ---------------------------------------------------------------------------

function UploadStep({
  dragOver,
  onDragOver,
  onDrop,
  onFileInput,
  fileInputRef,
  error,
}: {
  dragOver: boolean
  onDragOver: (v: boolean) => void
  onDrop: (e: React.DragEvent) => void
  onFileInput: (e: React.ChangeEvent<HTMLInputElement>) => void
  fileInputRef: React.RefObject<HTMLInputElement>
  error: string | null
}) {
  return (
    <div className="p-6">
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Upload your file</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Import from CSV or JSON files. Supports exports from LinkedIn, Notion, Airtable, Google Sheets, and most ATS systems.
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); onDragOver(true) }}
        onDragLeave={() => onDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-all ${
          dragOver
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800/50'
        }`}
      >
        <div className="flex flex-col items-center gap-3">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
            dragOver ? 'bg-blue-100 dark:bg-blue-500/20' : 'bg-gray-100 dark:bg-gray-800'
          }`}>
            <Upload className={`w-7 h-7 ${dragOver ? 'text-blue-600' : 'text-gray-400'}`} />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Drag & drop your file here, or <span className="text-blue-600">browse</span>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">CSV and JSON files supported</p>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.json"
          onChange={onFileInput}
          className="hidden"
        />
      </div>

      {error && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Format hints */}
      <div className="mt-6 grid grid-cols-2 gap-3">
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Table className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">CSV Format</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            First row should be column headers. Common columns: Company, Position, Date Applied, Status
          </p>
        </div>
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">JSON Format</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Array of objects with fields like company, position, appliedDate, stage, notes
          </p>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 2: Column Mapping
// ---------------------------------------------------------------------------

function MappingStep({
  parsedData,
  mappings,
  onMappingsChange,
  fileName,
}: {
  parsedData: ParsedData
  mappings: ColumnMapping[]
  onMappingsChange: (m: ColumnMapping[]) => void
  fileName: string
}) {
  function updateMapping(fileColumn: string, appField: string) {
    const existing = mappings.findIndex(m => m.fileColumn === fileColumn)

    if (appField === '') {
      // Remove mapping
      if (existing >= 0) {
        onMappingsChange(mappings.filter(m => m.fileColumn !== fileColumn))
      }
      return
    }

    // Remove any existing mapping for this appField from another column
    const filtered = mappings.filter(m => m.appField !== appField || m.fileColumn === fileColumn)

    if (existing >= 0) {
      const updated = [...filtered]
      const idx = updated.findIndex(m => m.fileColumn === fileColumn)
      if (idx >= 0) {
        updated[idx] = { fileColumn, appField }
      }
      onMappingsChange(updated)
    } else {
      onMappingsChange([...filtered, { fileColumn, appField }])
    }
  }

  const requiredMapped = MAPPABLE_FIELDS
    .filter(f => f.required)
    .every(f => mappings.some(m => m.appField === f.key))

  return (
    <div className="p-6">
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Map your columns</h4>
          <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
            {fileName}
          </span>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Match your file's columns to application fields. We auto-detected some mappings — adjust as needed.
        </p>
      </div>

      {!requiredMapped && (
        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-amber-700 dark:text-amber-400">
            Company and Position are required. Please map at least these two fields.
          </p>
        </div>
      )}

      {/* Column mapping table */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-800">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Your Column
              </th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Sample Data
              </th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                Maps To
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {parsedData.headers.map(header => {
              const currentMapping = mappings.find(m => m.fileColumn === header)
              const sampleValues = parsedData.rows.slice(0, 3)
                .map(r => r[header])
                .filter(Boolean)
                .join(', ')

              return (
                <tr key={header} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Copy className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{header}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 max-w-[200px]">
                      {sampleValues || <span className="italic">empty</span>}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={currentMapping?.appField || ''}
                      onChange={e => updateMapping(header, e.target.value)}
                      className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                    >
                      <option value="">-- Skip --</option>
                      {MAPPABLE_FIELDS.map(field => (
                        <option key={field.key} value={field.key}>
                          {field.label}{field.required ? ' *' : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
        {parsedData.totalRows} rows found in file. {mappings.length} columns mapped.
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 3: Preview
// ---------------------------------------------------------------------------

function PreviewStep({
  preview,
  skipDuplicates,
  onSkipDuplicatesChange,
}: {
  preview: ImportPreview
  skipDuplicates: boolean
  onSkipDuplicatesChange: (v: boolean) => void
}) {
  const displayRows = preview.validations.filter(v => {
    if (!v.isValid) return false
    if (skipDuplicates && v.isDuplicate) return false
    return true
  })

  return (
    <div className="p-6">
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Preview import</h4>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Review the data before importing. {displayRows.length} applications will be imported.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <StatCard label="Total Rows" value={preview.totalRows} color="gray" />
        <StatCard label="Valid" value={preview.validRows} color="emerald" />
        <StatCard label="Invalid" value={preview.invalidRows} color="red" />
        <StatCard label="Duplicates" value={preview.duplicateCount} color="amber" />
      </div>

      {preview.missingRequired.length > 0 && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-red-700 dark:text-red-400">
            <p className="font-medium">Required fields not mapped:</p>
            <p>{preview.missingRequired.join(', ')}</p>
          </div>
        </div>
      )}

      {/* Duplicate handling */}
      {preview.duplicateCount > 0 && (
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={skipDuplicates}
            onChange={e => onSkipDuplicatesChange(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Skip {preview.duplicateCount} duplicate{preview.duplicateCount !== 1 ? 's' : ''} (same company + position + date)
          </span>
        </label>
      )}

      {/* Preview table */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">#</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Company</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Position</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Date</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {preview.validations.slice(0, 50).map(v => {
                const isHidden = !v.isValid || (skipDuplicates && v.isDuplicate)
                return (
                  <tr
                    key={v.rowIndex}
                    className={`text-sm ${isHidden ? 'opacity-40' : ''}`}
                  >
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{v.rowIndex + 1}</td>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{v.mappedData.company || '—'}</td>
                    <td className="px-3 py-2 text-gray-900 dark:text-gray-100">{v.mappedData.position || '—'}</td>
                    <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{v.mappedData.appliedDate || '—'}</td>
                    <td className="px-3 py-2">
                      {v.isDuplicate ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400">
                          Duplicate
                        </span>
                      ) : !v.isValid ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400">
                          Invalid
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                          New
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {preview.totalRows > 50 && (
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 text-center">
            Showing first 50 of {preview.totalRows} rows
          </div>
        )}
      </div>

      {/* Errors */}
      {preview.invalidRows > 0 && (
        <div className="mt-3">
          <details className="text-sm">
            <summary className="cursor-pointer text-red-600 dark:text-red-400 font-medium">
              {preview.invalidRows} row{preview.invalidRows !== 1 ? 's' : ''} with errors
            </summary>
            <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
              {preview.validations.filter(v => !v.isValid).map(v => (
                <p key={v.rowIndex} className="text-xs text-red-600 dark:text-red-400">
                  Row {v.rowIndex + 1}: {v.errors.join('; ')}
                </p>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Step 4: Results
// ---------------------------------------------------------------------------

function ResultStep({ result }: { result: ImportResult }) {
  const hasErrors = result.failed > 0

  return (
    <div className="p-6">
      <div className="text-center py-8">
        {result.succeeded > 0 ? (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Import Complete
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Successfully imported {result.succeeded} application{result.succeeded !== 1 ? 's' : ''}.
            </p>
          </>
        ) : (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Import Failed
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No applications could be imported. See errors below.
            </p>
          </>
        )}
      </div>

      {/* Result stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatCard label="Imported" value={result.succeeded} color="emerald" />
        <StatCard label="Failed" value={result.failed} color="red" />
        <StatCard label="Skipped" value={result.skippedDuplicates} color="amber" />
      </div>

      {/* Errors */}
      {hasErrors && result.errors.length > 0 && (
        <div className="mt-2">
          <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">Errors:</h5>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {result.errors.map((err, i) => (
              <p key={i} className="text-xs text-red-600 dark:text-red-400">
                Row {err.rowIndex + 1}: {err.message}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Stat card helper
// ---------------------------------------------------------------------------

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  const colorMap: Record<string, string> = {
    gray: 'bg-gray-50 dark:bg-gray-800 text-gray-900 dark:text-gray-100',
    emerald: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    red: 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400',
    amber: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400',
  }

  return (
    <div className={`rounded-lg p-3 text-center ${colorMap[color] || colorMap.gray}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs font-medium opacity-75">{label}</p>
    </div>
  )
}
